import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
import React, { useState, useEffect, useCallback } from 'react';
import { Edit, Trash2, Plus, History, ShieldPlus } from 'lucide-react';
import { fetchConfiguracionesPago, ConfiguracionPago} from '../../../services/configuracionPagoService';
import AddConfigPagos from '../../../components/ConfigPagos/AddConfigPagos';
import EditConfigPagos from '../../../components/ConfigPagos/EditConfigPagos';
import DeleteConfigPagos from '../../../components/ConfigPagos/DeleteConfigPagos';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';
import Loading from '../../../components/Loading';
import { useNavigate } from 'react-router-dom';

const ConfigPagosPage: React.FC = () => {
    const [configuraciones, setConfiguraciones] = useState<ConfiguracionPago[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    
    // Estados para modales
    const [modalAbierto, setModalAbierto] = useState(false);
    const [modalEditarAbierto, setModalEditarAbierto] = useState(false);
    const [modalEliminarAbierto, setModalEliminarAbierto] = useState(false);
    const [configuracionEditando, setConfiguracionEditando] = useState<ConfiguracionPago | null>(null);
    const [configuracionEliminando, setConfiguracionEliminando] = useState<ConfiguracionPago | null>(null);

    // Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Sesión expirada
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const navigate = useNavigate();

    const userLevelId = user?.user_level_id ?? 0;
    // Ajusta el nombre del modelo según tu backend (p.ej. 'configuracionpago', 'configuracion_pago')
    const perm = useModelPermission('configuracionpago', userLevelId);

    // Función para cargar las configuraciones
    const cargarConfiguraciones = useCallback(async () => {
        setLoading(true);
        try {
            if (perm?.can_read) {
                const data = await fetchConfiguracionesPago();
                const dataOrdenada = [...data].sort((a, b) => a.id - b.id);
                setConfiguraciones(dataOrdenada);
                setError(null);
            } else {
                setConfiguraciones([]);
            }
        } catch (err) {
            setError('Error al cargar las configuraciones de pago');
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm]);

    useEffect(() => {
        if (perm === null) return; // esperar a que los permisos se carguen
        cargarConfiguraciones();
    }, [perm, cargarConfiguraciones]);

    // Funciones para abrir modales
    const abrirModalEdicion = (config: ConfiguracionPago) => {
        setConfiguracionEditando(config);
        setModalEditarAbierto(true);
    };

    const abrirModalEliminacion = (config: ConfiguracionPago) => {
        setConfiguracionEliminando(config);
        setModalEliminarAbierto(true);
    };

    // Funciones de actualización optimista
    const manejarConfiguracionCreada = async (nuevaConfiguracion: ConfiguracionPago) => {
        try {
            setConfiguraciones(prev => [...prev, nuevaConfiguracion].sort((a, b) => a.id - b.id));
            setModalAbierto(false);
        } catch (err) {
            /* Browser diagnostics omitted. */
            cargarConfiguraciones();
        }
    };

    const manejarConfiguracionActualizada = async (configActualizada: ConfiguracionPago) => {
        try {
            setConfiguraciones(prev => 
                prev.map(config => 
                    config.id === configActualizada.id ? configActualizada : config
                ).sort((a, b) => a.id - b.id)
            );
            setModalEditarAbierto(false);
            setConfiguracionEditando(null);
        } catch (err) {
            /* Browser diagnostics omitted. */
            cargarConfiguraciones();
        }
    };

    const manejarConfiguracionEliminada = async (id: number) => {
        try {
            setConfiguraciones(prev => prev.filter(config => config.id !== id));
            setModalEliminarAbierto(false);
            setConfiguracionEliminando(null);
        } catch (err) {
            /* Browser diagnostics omitted. */
            cargarConfiguraciones();
        }
    };

    const totalPages = Math.ceil(configuraciones.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedConfiguraciones = configuraciones.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null || (loading && configuraciones.length === 0)) {
        return <Loading text="Cargando configuración de pagos..." />;
    }

    return <><LumaResourceWorkspace title="Configuración de Pagos" description="Reglas de cobro y movimientos de caja" group="Configuraciones" rows={configuraciones} columns={[{key:'nombre',label:'Nombre'},{key:'tipo',label:'Tipo'},{key:'afecta_caja',label:'Afecta caja'},{key:'requiere_cambio',label:'Requiere cambio'},{key:'activo',label:'Estado'}]} idKey="id" nameKey="nombre" statusKey="activo" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setModalAbierto(true)}:undefined} onEdit={perm?.can_update?r=>{setConfiguracionEditando(r);setModalEditarAbierto(true);}:undefined} onDelete={perm?.can_delete?r=>{setConfiguracionEliminando(r);setModalEliminarAbierto(true);}:undefined} history=""/><ExpiredSessionModal
                isOpen={isSessionExpired}
                onClose={async () => {
                    setIsSessionExpired(false);
                    await logout();
                }}
            /><AddConfigPagos
                isOpen={modalAbierto}
                onClose={() => setModalAbierto(false)}
                onConfiguracionCreated={manejarConfiguracionCreada}
            /><EditConfigPagos
                isOpen={modalEditarAbierto}
                onClose={() => {
                    setModalEditarAbierto(false);
                    setConfiguracionEditando(null);
                }}
                onConfiguracionUpdated={manejarConfiguracionActualizada}
                configuracion={configuracionEditando}
            /><DeleteConfigPagos
                isOpen={modalEliminarAbierto}
                onClose={() => {
                    setModalEliminarAbierto(false);
                    setConfiguracionEliminando(null);
                }}
                onConfiguracionDeleted={manejarConfiguracionEliminada}
                configuracion={configuracionEliminando}
            /></>;
};

export default ConfigPagosPage;
